import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { emptyFilters, filtersFromParams, filtersToParams, type Filters } from "./filterState";

/**
 * Filters live in the URL rather than a store.
 *
 * Telegram's back button walks browser history, so anything not in the URL
 * silently survives a "back" that visually undid it.
 */
export function useFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo(() => filtersFromParams(params), [params]);

  const setFilters = useCallback(
    (next: Filters, options?: { replace?: boolean }) => {
      setParams(filtersToParams(next), { replace: options?.replace ?? true });
    },
    [setParams],
  );

  const patch = useCallback(
    (changes: Partial<Filters>) => setFilters({ ...filters, ...changes }),
    [filters, setFilters],
  );

  /** Keeps `q` — Clear all is about filters, and the search box is visible. */
  const clearFilters = useCallback(
    () => setFilters({ ...emptyFilters, q: filters.q }),
    [filters.q, setFilters],
  );

  const clearAll = useCallback(() => setFilters(emptyFilters), [setFilters]);

  return { filters, setFilters, patch, clearFilters, clearAll };
}
